





import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLoader } from '../../../../context/LoaderProvider.jsx';

// Delivery Partner specific field configuration
const getDefaultDeliveryPartnerRows = () => [
    { key: "Full Name", value: "", isUpload: false },
    { key: "Phone Number", value: "", isUpload: false },
    { key: "Email", value: "", isUpload: false },
    { key: "Profile Picture", value: null, isUpload: false },
    { key: "AADHAR Number", value: "", isUpload: false },
    { key: "PAN Number", value: "", isUpload: false },
    { key: "License Number", value: "", isUpload: false },
    { key: "Vehicle Types", value: [], isUpload: false },
    { key: "Vehicles", value: [], isUpload: false },
    { key: "Petrolium Types", value: [], isUpload: false },
    { key: "RC Number", value: "", isUpload: false },
    { key: "Insurance Number", value: "", isUpload: false },
    {
        key: "Bank Details", value: {
            bankName: "",
            accountNumber: "",
            accountHolderName: "",
            ifscCode: "",
            branchName: "",
            upiId: ""
        }, isUpload: false
    },
    {
        key: "Current Address", value: {
            address: "",
            city: "",
            state: "",
            pinCode: "",
            country: "",
            houseNo: "",
            street: ""
        }, isUpload: false
    },
    {
        key: "Permanent Address", value: {
            permanentAddress: "",
            permanentCity: "",
            permanentState: "",
            permanentPinCode: "",
            permanentCountry: "",
            permanentHouseNo: "",
            permanentStreet: ""
        }, isUpload: false
    }
];

// Vehicle Types options
const vehicleTypeOptions = [
    "BIKE",
    "PASSENGER_AUTO",
    "CAR",
    "MINI_TRUCK",
    "TRUCK"
];

// Vehicles options
const vehiclesOptions = [
    "TwoWheeler",
    "ThreeWheeler",
    "FourWheeler",
    "HeavyVehicle"
];

// Petroleum Types options
const petroliumTypeOptions = [
    "PETROL",
    "DIESEL",
    "CNG",
    "ELECTRIC"
];

// Status field mapping for delivery partners
const STATUS_FIELD_MAP = {
    "Full Name": "fullNameStatus",
    "Phone Number": "phoneNumberStatus",
    "Email": "emailStatus",
    "Profile Picture": "profilePictureStatus",
    "AADHAR Number": "aadharNumberStatus",
    "PAN Number": "panNumberStatus",
    "License Number": "licenseNumberStatus",
    "Vehicle Types": "vehicleTypesStatus",
    "Vehicles": "vehiclesStatus",
    "Petrolium Types": "petroliumTypesStatus",
    "RC Number": "rcNumberStatus",
    "Insurance Number": "insuranceNumberStatus",
    "Bank Details": "bankDetailsStatus",
    "Current Address": "addressStatus",
    "Permanent Address": "permanentAddressStatus"
};

// Safe value display utility
const safeDisplay = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === 'string' && value.trim() === "") return "N/A";
    if (value === "N/A" || value === "Invalid Date") return "N/A";
    return value;
};

// Add this function to check if a field has valid data
const hasValidData = (row, partnerDetails) => {
    if (!row || !partnerDetails) return false;

    const field = row.key;
    const value = row.value;

    // Check if the value exists and is not empty/"N/A"
    if (typeof value === 'string') {
        return value && value !== "N/A" && value.trim() !== "";
    }

    if (Array.isArray(value)) {
        return value.length > 0 && value.some(item => item && item !== "N/A");
    }

    if (typeof value === 'object') {
        // For objects, check if any property has valid data
        return Object.values(value).some(val => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val.trim() !== "" && val !== "N/A";
            if (Array.isArray(val)) return val.length > 0;
            return true;
        });
    }

    return false;
};

const isFieldVerified = (rowKey, rowValue, partnerDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !partnerDetails) return false;

    if (partnerDetails[statusField] === true) return true;

    if ((partnerDetails[statusField] === null || partnerDetails[statusField] === undefined) &&
        hasValidData({ key: rowKey, value: rowValue }, partnerDetails)) {
        return true;
    }

    return false;
};

const isFieldRejected = (rowKey, partnerDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !partnerDetails) return false;

    return partnerDetails[statusField] === false;
};

/* ---------------- component ---------------- */
const DeliveryPartnerManagement = () => {
    /* -------- list‑screen state -------- */
    const [partnersData, setPartnersData] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [vehicleFilter, setVehicleFilter] = useState("");
    const [cityFilter, setCityFilter] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const { showLoader, hideLoader } = useLoader();

    // New search filter states
    const [searchType, setSearchType] = useState("partnerId");
    const [searchQuery, setSearchQuery] = useState("");

    /* -------- detail‑screen state -------- */
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerDetails, setPartnerDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState(getDefaultDeliveryPartnerRows());
    const [verifiedRows, setVerifiedRows] = useState([]);
    const [rejectedRows, setRejectedRows] = useState([]);
    const [isApproved, setIsApproved] = useState(false);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

    const fileInputRef = useRef(null);
    const [partnerData, setPartnerData] = useState({});
    const [rowId, setRowId] = useState(null);

    // State for reject modal/dropdown
    const [showRejectDropdown, setShowRejectDropdown] = useState(false);
    const [rejectionDescription, setRejectionDescription] = useState("");

    // Add loading states for approve and reject
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    // Add refresh key for forcing updates
    const [refreshKey, setRefreshKey] = useState(0);

    const getCurrentPartnerId = () => {
        return (
            selectedPartner?.id ||
            selectedPartner?.partnerId ||
            partnerDetails?.id ||
            null
        );
    };

    const partnerId = getCurrentPartnerId();

    const handleImageError = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => new Set([...prev, errorKey]));
    };

    const handleImageLoad = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(errorKey);
            return newSet;
        });
    };

    const isImageFailed = (fieldName, index) => {
        return imageLoadErrors.has(`${fieldName}-${index}`);
    };

    const fetchPartnerDetails = async (partnerId) => {
        if (!partnerId) return;
        setLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            console.log(`🔍 FETCHING PARTNER DETAILS FOR ID: ${partnerId}`);

            // Use the working API endpoint  
            const response = await axios.get(
                `https://backend.maamaas.com/delivery/api/get/${partnerId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("✅ API RESPONSE RECEIVED:", response.data);
            setPartnerDetails(response.data);

        } catch (err) {
            console.error("❌ get-partner-details error:", err);

            // Fall back to list data if detailed fetch fails
            const listPartner = partnersData.find(p =>
                p.id === partnerId || p.partnerId === partnerId
            );
            if (listPartner) {
                console.log("⚠️ Falling back to list data:", listPartner);
                setPartnerDetails(listPartner);
            }
        } finally {
            setLoading(false);
        }
    };

    // Function to refresh all data
    const refreshAllData = async () => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            console.error("No auth token found");
            return;
        }

        showLoader();

        try {
            // Fetch all delivery partners
            const response = await axios.get("https://backend.maamaas.com/delivery/api/admin/get/all", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            console.log("📊 Refreshed partners data:", response.data);
            setPartnersData(response.data);

            // If we have a selected partner, refresh their details too
            if (selectedPartner) {
                const currentPartnerId = selectedPartner?.id || selectedPartner?.partnerId;
                if (currentPartnerId) {
                    await fetchPartnerDetails(currentPartnerId);
                }
            }

        } catch (err) {
            console.error("Refresh data error:", err);
        } finally {
            hideLoader();
        }
    };

    // Save row using the update API endpoint
    const handleSaveRow = async (rowIndex) => {
        const token = localStorage.getItem("authToken") || localStorage.getItem("token");

        if (!token) {
            alert("No authentication token found");
            return;
        }

        try {
            const partnerId = getCurrentPartnerId();
            if (!partnerId) {
                console.error("Partner ID not found");
                return;
            }

            const row = rows[rowIndex];
            console.log("Editing row:", row);

            // Create FormData
            const formData = new FormData();

            // Create partnerData object
            const partnerData = {};

            // Map row keys to actual backend field names
            const fieldMapping = {
                "Full Name": "fullName",
                "Phone Number": "phoneNumber",
                "Email": "email",
                "AADHAR Number": "aadharNumber",
                "PAN Number": "panNumber",
                "License Number": "licenseNumber",
                "Vehicle Types": "vehicleTypes",
                "Vehicles": "vehicles",
                "Petrolium Types": "petroliumTypes",
                "RC Number": "rcNumber",
                "Insurance Number": "insuranceNumber",
            };

            // Add the specific field being edited
            const backendFieldName = fieldMapping[row.key];
            if (backendFieldName && row.value && row.value !== "N/A") {
                if (Array.isArray(row.value)) {
                    partnerData[backendFieldName] = row.value;
                } else if (typeof row.value === 'string') {
                    partnerData[backendFieldName] = row.value;
                }
            }

            // Handle address fields
            if (row.key === "Current Address" && typeof row.value === "object") {
                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            if (row.key === "Permanent Address" && typeof row.value === "object") {
                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            if (row.key === "Bank Details" && typeof row.value === "object") {
                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            // Append partnerData as JSON
            formData.append("partnerData", new Blob([JSON.stringify(partnerData)], {
                type: "application/json"
            }));

            // Handle file uploads
            if (row.images && row.images.length > 0) {
                row.images.forEach((img, idx) => {
                    if (img.file) {
                        const imageFieldName = getImageFieldName(row.key, idx);
                        formData.append(imageFieldName, img.file);
                    }
                });
            }

            // Use the update API endpoint
            const response = await axios.put(
                `https://backend.maamaas.com/delivery/api/registration/update/${partnerId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            alert("Data updated successfully!");

            setRows(prev =>
                prev.map((r, idx) => (idx === rowIndex ? { ...r, isEditing: false } : r))
            );

            // Refresh the data
            fetchPartnerDetails(partnerId);

        } catch (err) {
            console.error("Error updating partner:", err);
            alert("Error updating data: " + (err.response?.data?.message || err.message));
        }
    };

    const getImageFieldName = (rowKey, imageIndex) => {
        const fieldMap = {
            "AADHAR Number": imageIndex === 0 ? "aadharCardFront" : "aadharCardBack",
            "PAN Number": "panCard",
            "License Number": imageIndex === 0 ? "licenseFront" : "licenseBack",
            "Profile Picture": "profilePicture",
            "RC Number": imageIndex === 0 ? "rcBookFront" : "rcBookBack",
        };

        return fieldMap[rowKey] || `image${imageIndex + 1}`;
    };

    // Search options for dropdown
    const searchOptions = [
        { value: "partnerId", label: "Partner ID" },
        { value: "fullName", label: "Full Name" },
        { value: "email", label: "Email" },
        { value: "phoneNumber", label: "Mobile Number" },
        { value: "city", label: "City" }
    ];

    // Get placeholder text based on search type
    const getPlaceholderText = () => {
        switch (searchType) {
            case "partnerId": return "Search Partner ID";
            case "fullName": return "Search Full Name";
            case "email": return "Search Email";
            case "phoneNumber": return "Search Mobile Number";
            case "city": return "Search City";
            default: return "Search...";
        }
    };

    // Clear all search filters
    const clearAllFilters = () => {
        setSearchQuery("");
        setStatusFilter("");
        setVehicleFilter("");
        setCityFilter("");
    };

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            console.error("No auth token found");
            return;
        }

        showLoader();

        // Fetch all delivery partners
        axios.get("https://backend.maamaas.com/delivery/api/admin/get/all", {
            headers: {
                "Authorization": `Bearer ${token}`
            }
        })
            .then((response) => {
                console.log("📊 Initial partners data:", response.data);
                setPartnersData(response.data);
            })
            .catch((err) => console.error("Partners fetch error:", err))
            .finally(() => {
                hideLoader();
            });
    }, [refreshKey]);

    useEffect(() => {
        let data = partnersData;

        // Apply status filter
        if (statusFilter) {
            if (statusFilter === "verified") {
                data = data.filter((i) => i.verified === true);
            } else if (statusFilter === "not-verified") {
                data = data.filter((i) => i.verified === false);
            }
        }

        // Apply vehicle filter
        if (vehicleFilter) {
            data = data.filter((i) =>
                i.vehicleTypes?.includes(vehicleFilter) ||
                i.vehicles?.includes(vehicleFilter)
            );
        }

        // Apply city filter
        if (cityFilter) {
            data = data.filter((i) => i.city === cityFilter);
        }

        // Apply search query filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            data = data.filter(partner => {
                // Get the field value based on searchType
                let fieldValue;

                // Handle different field names in the data
                if (searchType === "phoneNumber") {
                    fieldValue = partner.phoneNumber || partner.maskedPhoneNumber;
                } else if (searchType === "email") {
                    fieldValue = partner.email || partner.maskedEmail;
                } else {
                    fieldValue = partner[searchType];
                }

                if (!fieldValue) return false;

                // Special handling for mobile number search
                if (searchType === "phoneNumber") {
                    const cleanPartnerMobile = fieldValue.toString().replace(/\D/g, '');
                    const cleanQuery = query.replace(/\D/g, '');
                    return fieldValue.toString().toLowerCase().includes(query) ||
                        cleanPartnerMobile.includes(cleanQuery);
                }

                // For other fields
                return fieldValue.toString().toLowerCase().includes(query);
            });
        }

        setFilteredData(data);
    }, [partnersData, statusFilter, vehicleFilter, cityFilter, searchType, searchQuery]);

    useEffect(() => {
        if (!selectedPartner) {
            setRows(getDefaultDeliveryPartnerRows());
            setVerifiedRows([]);
            setRejectedRows([]);
            setIsApproved(false);
            setImageLoadErrors(new Set());
            return;
        }

        // Use partnerDetails as primary data source
        const dataSource = partnerDetails || selectedPartner;

        console.log("📊 Data source for detail view:", dataSource);

        // Check if partner is approved
        if (dataSource.verified === true) {
            setIsApproved(true);
        }

        // Image mapping for delivery partners
        const imageMap = {
            "AADHAR Number": [
                dataSource.aadharCardFront,
                dataSource.aadharCardBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "PAN Number": [dataSource.panCard]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "License Number": [
                dataSource.licenseFront,
                dataSource.licenseBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Profile Picture": [dataSource.profilePicture]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "RC Number": [
                dataSource.rcBookFront,
                dataSource.rcBookBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
        };

        // Create rows with actual data from the backend response
        const newRows = [
            {
                key: "Full Name",
                value: dataSource.fullName || "N/A"
            },
            {
                key: "Phone Number",
                value: dataSource.phoneNumber || "N/A"
            },
            {
                key: "Email",
                value: dataSource.email || "N/A"
            },
            {
                key: "Profile Picture",
                value: "Profile Picture"
            },
            {
                key: "AADHAR Number",
                value: dataSource.aadharNumber || "N/A"
            },
            {
                key: "PAN Number",
                value: dataSource.panNumber || "N/A"
            },
            {
                key: "License Number",
                value: dataSource.licenseNumber || "N/A"
            },
            {
                key: "Vehicle Types",
                value: Array.isArray(dataSource.vehicleTypes)
                    ? dataSource.vehicleTypes
                    : (dataSource.vehicleTypes ? [dataSource.vehicleTypes] : [])
            },
            {
                key: "Vehicles",
                value: Array.isArray(dataSource.vehicles)
                    ? dataSource.vehicles
                    : (dataSource.vehicles ? [dataSource.vehicles] : [])
            },
            {
                key: "Petrolium Types",
                value: Array.isArray(dataSource.petroliumTypes)
                    ? dataSource.petroliumTypes
                    : (dataSource.petroliumTypes ? [dataSource.petroliumTypes] : [])
            },
            {
                key: "RC Number",
                value: Array.isArray(dataSource.rcNumber) && dataSource.rcNumber.length > 0
                    ? dataSource.rcNumber[0]
                    : dataSource.rcNumber || "N/A"
            },
            {
                key: "Insurance Number",
                value: Array.isArray(dataSource.insuranceNumber) && dataSource.insuranceNumber.length > 0
                    ? dataSource.insuranceNumber[0]
                    : dataSource.insuranceNumber || "N/A"
            },
            {
                key: "Bank Details",
                value: {
                    bankName: dataSource.bankName || "N/A",
                    accountNumber: dataSource.accountNumber || "N/A",
                    accountHolderName: dataSource.accountHolderName || "N/A",
                    ifscCode: dataSource.ifscCode || "N/A",
                    branchName: dataSource.branchName || "N/A",
                    upiId: dataSource.upiId || "N/A",
                },
            },
            {
                key: "Current Address",
                value: {
                    address: dataSource.address || "N/A",
                    city: dataSource.city || "N/A",
                    state: dataSource.state || "N/A",
                    pinCode: dataSource.pinCode || "N/A",
                    country: dataSource.country || "N/A",
                    houseNo: dataSource.houseNo || "N/A",
                    street: dataSource.street || "N/A",
                },
            },
            {
                key: "Permanent Address",
                value: {
                    permanentAddress: dataSource.permanentAddress || "N/A",
                    permanentCity: dataSource.permanentCity || "N/A",
                    permanentState: dataSource.permanentState || "N/A",
                    permanentPinCode: dataSource.permanentPinCode || "N/A",
                    permanentCountry: dataSource.permanentCountry || "N/A",
                    permanentHouseNo: dataSource.permanentHouseNo || "N/A",
                    permanentStreet: dataSource.permanentStreet || "N/A",
                },
            },
        ];

        const updatedRows = newRows.map((row) => ({
            ...row,
            images: imageMap[row.key] || [],
        }));

        console.log("🚨 DEBUG - Final rows to display:", updatedRows);

        setRows(updatedRows.map((r) => ({ ...r, isEditing: false })));
        setVerifiedRows([]);
        setRejectedRows([]);
        setImageLoadErrors(new Set());
    }, [selectedPartner, partnerDetails]);

    const handleViewClick = (partner) => {
        console.log("👁️ VIEW CLICKED FOR PARTNER:", partner);
        setSelectedPartner(partner);
        const partnerId = partner?.id || partner?.partnerId;
        console.log("🔍 FETCHING PARTNER DETAILS FOR ID:", partnerId);
        fetchPartnerDetails(partnerId);
    };

    // Verify individual field using the new API
    const handleVerifyy = async (i) => {
        const normalisedKey = rows[i].key.trim().replace(/\.$/, "");
        const statusField = STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) {
            console.warn("No status mapping for:", rows[i].key);
            return;
        }

        const partnerId = getCurrentPartnerId();
        if (!partnerId) return alert("Partner ID is missing");

        try {
            // Prepare data for verification
            const updateData = {
                [statusField]: true
            };

            // Create FormData
            const formData = new FormData();
            formData.append("partnerData", new Blob([JSON.stringify(updateData)], {
                type: "application/json"
            }));

            // Use the update API endpoint
            const response = await axios.put(
                `https://backend.maamaas.com/delivery/api/registration/update/${partnerId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            await fetchPartnerDetails(partnerId);
            alert("Partner field verified successfully!");
        } catch (err) {
            console.error("❌ Verify error:", err);
            alert(`Failed to verify field: ${err.message}`);
        }
    };

    // Reject individual field using the new API
    const handleReject = async (i) => {
        const normalisedKey = rows[i].key.trim().replace(/\.$/, "");
        const statusField = STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) {
            return;
        }

        const partnerId = getCurrentPartnerId();
        if (!partnerId) return alert("Partner ID is missing");

        try {
            // Prepare data for rejection
            const updateData = {
                [statusField]: false
            };

            // Create FormData
            const formData = new FormData();
            formData.append("partnerData", new Blob([JSON.stringify(updateData)], {
                type: "application/json"
            }));

            // Use the update API endpoint
            const response = await axios.put(
                `https://backend.maamaas.com/delivery/api/registration/update/${partnerId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            alert("Partner field rejected successfully!");
            fetchPartnerDetails(partnerId);
        } catch (err) {
            console.error("❌ Reject error:", err);
            alert(`Failed to reject field: ${err.message}`);
        }
    };

    // Approve all using the /admin/approval/{id} API endpoint
    const handleApproveAll = async () => {
        const partnerId = getCurrentPartnerId();
        if (!partnerId) return alert("Partner ID is missing");

        setIsApproving(true);

        try {
            const token = localStorage.getItem("authToken");
            if (!token) return alert("Authorization token missing");

            console.log(`✅ Approving partner ${partnerId}...`);
            console.log('Request payload:', { approved: true });

            // Use the /admin/approval/{id} API endpoint for approval
            const response = await axios.put(
                `https://backend.maamaas.com/delivery/api/admin/approval/${partnerId}`,
                { approved: true },
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("✅ Approval API response:", response.data);
            console.log("Verified status in response:", response.data.verified);

            // Check if the response actually has verified: true
            if (response.data && response.data.verified === true) {
                alert("Partner approved successfully!");

                // Update all states with verified: true
                setPartnerDetails(prev => ({
                    ...prev,
                    ...response.data,
                    verified: true
                }));

                setSelectedPartner(prev => ({
                    ...prev,
                    ...response.data,
                    verified: true
                }));

                setPartnersData(prevPartners =>
                    prevPartners.map(partner =>
                        partner.id === partnerId || partner.partnerId === partnerId
                            ? {
                                ...partner,
                                ...response.data,
                                verified: true
                            }
                            : partner
                    )
                );

                setIsApproved(true);

                // Force refresh after 1 second to get latest data
                setTimeout(() => {
                    setRefreshKey(prev => prev + 1);
                }, 1000);

            } else if (response.data) {
                // If we get data but verified is still false
                console.warn("API returned data but verified is still false:", response.data.verified);

                // Try different payload formats
                console.log("Trying different payload formats...");
                const payloadFormats = [
                    { verified: true },
                    { status: "approved" },
                    { isVerified: true },
                    { verificationStatus: "APPROVED" },
                    { approvalStatus: "APPROVED" }
                ];

                let success = false;

                for (const payload of payloadFormats) {
                    try {
                        console.log("Trying payload:", payload);
                        const testResponse = await axios.put(
                            `https://backend.maamaas.com/delivery/api/admin/approval/${partnerId}`,
                            payload,
                            {
                                headers: {
                                    "Authorization": `Bearer ${token}`,
                                    "Content-Type": "application/json"
                                }
                            }
                        );

                        if (testResponse.data && testResponse.data.verified === true) {
                            console.log("✅ Success with payload:", payload);

                            // MOVE STATE UPDATES HERE - only on success
                            setPartnerDetails(prev => ({
                                ...prev,
                                ...testResponse.data,
                                verified: true
                            }));

                            setSelectedPartner(prev => ({
                                ...prev,
                                ...testResponse.data,
                                verified: true
                            }));

                            setPartnersData(prevPartners =>
                                prevPartners.map(partner =>
                                    partner.id === partnerId || partner.partnerId === partnerId
                                        ? {
                                            ...partner,
                                            ...testResponse.data,
                                            verified: true
                                        }
                                        : partner
                                )
                            );

                            setIsApproved(true);
                            success = true;
                            alert("Partner approved successfully!");
                            break;
                        }
                    } catch (err) {
                        console.error("Error with payload", payload, ":", err.message);
                    }
                }

                if (!success) {
                    // Try alternative endpoints
                    console.log("Trying alternative endpoints...");
                    const alternativeEndpoints = [
                        `https://backend.maamaas.com/delivery/api/admin/verify/${partnerId}`,
                        `https://backend.maamaas.com/delivery/api/admin/approve/${partnerId}`,
                    ];

                    for (const endpoint of alternativeEndpoints) {
                        try {
                            console.log("Trying endpoint:", endpoint);
                            const endpointResponse = await axios.put(
                                endpoint,
                                { approved: true },
                                {
                                    headers: {
                                        "Authorization": `Bearer ${token}`,
                                        "Content-Type": "application/json"
                                    }
                                }
                            );

                            if (endpointResponse.data) {
                                console.log("✅ Response from alternative endpoint:", endpointResponse.data);

                                // MOVE STATE UPDATES HERE - only on success
                                setPartnerDetails(prev => ({
                                    ...prev,
                                    ...endpointResponse.data,
                                    verified: true
                                }));

                                setSelectedPartner(prev => ({
                                    ...prev,
                                    ...endpointResponse.data,
                                    verified: true
                                }));

                                setPartnersData(prevPartners =>
                                    prevPartners.map(partner =>
                                        partner.id === partnerId || partner.partnerId === partnerId
                                            ? {
                                                ...partner,
                                                ...endpointResponse.data,
                                                verified: true
                                            }
                                            : partner
                                    )
                                );

                                setIsApproved(true);
                                success = true;
                                alert("Partner approved with alternative method!");
                                break;
                            }
                        } catch (err) {
                            console.error("Error with endpoint", endpoint, ":", err.message);
                        }
                    }
                }

                if (success) {
                    setRefreshKey(prev => prev + 1);
                } else {
                    // Only show alert, don't update state if API fails
                    alert("⚠️ Backend API not responding properly. Please try again.");
                }

            } else {
                throw new Error("Invalid response from server");
            }

        } catch (err) {
            console.error("❌ Approve partner error:", err);

            // More detailed error logging
            if (err.response) {
                console.error("Response status:", err.response.status);
                console.error("Response data:", err.response.data);
                console.error("Response headers:", err.response.headers);
            }

            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to approve partner";
            alert(`Failed to approve partner: ${errorMessage}`);

        } finally {
            setIsApproving(false);
        }
    };

    // Reject all with reason using the /admin/approval/{id} API endpoint
    const handleRejectAll = async () => {
        if (!rejectionDescription.trim()) {
            alert("Please enter a rejection reason");
            return;
        }

        const partnerId = getCurrentPartnerId();
        if (!partnerId) return alert("Partner ID is missing");

        setIsRejecting(true);

        try {
            const token = localStorage.getItem("authToken");
            if (!token) return alert("Authorization token missing");

            // Use the /admin/approval/{id} API endpoint for rejection
            // CORRECTED: Use 'rejectReason' instead of 'rejectionReason'
            const requestBody = {
                verified: false, // Add verified field
                rejectReason: rejectionDescription.trim() // Use correct field name
            };


            const response = await axios.put(
                `https://backend.maamaas.com/delivery/api/admin/approval/${partnerId}`,
                requestBody,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("✅ Rejection API response:", response.data);

            setShowRejectDropdown(false);
            setRejectionDescription("");

            // Check if response indicates success
            if (response.data) {
                alert("Partner verification rejected successfully!");

                // Update partnersData array to reflect rejection
                setPartnersData(prevPartners =>
                    prevPartners.map(partner =>
                        partner.id === partnerId || partner.partnerId === partnerId
                            ? {
                                ...partner,
                                verified: false,
                                rejectReason: rejectionDescription // Store reject reason
                            }
                            : partner
                    )
                );

                // Update local state for the detail view
                setPartnerDetails(prev => ({
                    ...prev,
                    verified: false,
                    rejectReason: rejectionDescription
                }));

                setSelectedPartner(prev => ({
                    ...prev,
                    verified: false,
                    rejectReason: rejectionDescription
                }));

                setIsApproved(false);

                // Force refresh
                setTimeout(() => {
                    setRefreshKey(prev => prev + 1);
                }, 1000);
            } else {
                throw new Error("Invalid response from server");
            }

        } catch (err) {
            console.error("❌ Reject error:", err);

            // More detailed error message
            if (err.response) {
                console.error("Error response status:", err.response.status);
                console.error("Error response data:", err.response.data);
                console.error("Error response headers:", err.response.headers);
            }

            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to reject partner";
            alert(`Failed to reject: ${errorMessage}`);

        } finally {
            setIsRejecting(false);
        }
    };

    const handleBackClick = () => setSelectedPartner(null);

    const handleFileChange = (e, index) => {
        const fileName = e.target.files[0]?.name || "";
        const updated = [...rows];
        updated[index].value = fileName;
        setRows(updated);
    };

    // Clean Image Display Component with "-" for missing images
    const ImageDisplay = ({ images, fieldName }) => {
        if (!images || images.length === 0) {
            return (
                <div className="text-center">
                    <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                </div>
            );
        }

        return (
            <div className="d-flex justify-content-center gap-2 flex-wrap">
                {images.map((img, idx) => {
                    const imageUrl = typeof img === 'string' ? img : img.url;
                    const hasFailed = isImageFailed(fieldName, idx);

                    if (hasFailed || !imageUrl) {
                        return (
                            <div key={idx} className="text-center">
                                <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className="text-center position-relative">
                            <img
                                src={imageUrl}
                                alt={`${fieldName}-${idx}`}
                                style={{
                                    width: fieldName === "Profile Picture" ? "80px" : "60px",
                                    height: fieldName === "Profile Picture" ? "80px" : "60px",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    border: "1px solid #dee2e6",
                                }}
                                onError={() => handleImageError(imageUrl, fieldName, idx)}
                                onLoad={() => handleImageLoad(imageUrl, fieldName, idx)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12" style={{ marginTop: "10px" }}>
                    <div className="client" style={{ overflowY: "auto", height: "450px" }}>
                        {!selectedPartner ? (
                            <>
                                {/* Filter Controls - Updated like Userdetails file */}
                                <div className="row g-2 mb-1 align-items-center me-0">
                                    {/* Search Type Dropdown and Search Input - Side by Side */}
                                    <div className="col-md-auto col-12">
                                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>

                                            {/* Search Type Dropdown */}

                                            <div style={{ minWidth: '150px' }}>
                                                <select
                                                    className="form-select"
                                                    value={searchType}
                                                    onChange={(e) => setSearchType(e.target.value)}
                                                    style={{ height: "40px" }}
                                                >
                                                    {searchOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Search Input */}

                                            <div style={{ minWidth: '200px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={getPlaceholderText()}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    style={{ height: "40px" }}
                                                />
                                            </div>

                                        </div>
                                    </div>

                                    {/* Status Filter */}
                                    <div className="col-md-auto col-12">
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            aria-label="Filter by status"
                                            style={{ maxWidth: '150px', height: "40px" }}
                                        >
                                            <option value="">All Status</option>
                                            <option value="verified">Verified</option>
                                            <option value="not-verified">Not Verified</option>
                                        </select>
                                    </div>

                                    {/* Vehicle Filter */}
                                    <div className="col-md-auto col-12">
                                        <select
                                            className="form-select"
                                            value={vehicleFilter}
                                            onChange={(e) => setVehicleFilter(e.target.value)}
                                            aria-label="Filter by vehicle"
                                            style={{ maxWidth: '150px', height: "40px" }}
                                        >
                                            <option value="">All Vehicles</option>
                                            {vehicleTypeOptions.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>



                                    {/* Clear Filters Button */}
                                    <div className="col-md-auto col-12">
                                        <button
                                            className="btn"
                                            onClick={clearAllFilters}
                                            style={{
                                                backgroundColor: '#6c757d',
                                                color: '#fff',
                                                height: "40px",
                                                padding: "0 15px"
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>

                                    {/* Refresh Data Button */}
                                    <div className="col-md col-12">
                                        <div className="d-flex justify-content-start justify-content-md-end">
                                            <button
                                                className="btn"
                                                onClick={refreshAllData}
                                                style={{
                                                    backgroundColor: 'rgb(230 109 51)',
                                                    color: '#fff',
                                                    height: "40px",
                                                    padding: "0 20px"
                                                }}
                                            >
                                                Refresh Data
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="sed pe-3">
                                    <table style={tableStyle} className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>S.NO</th>
                                                <th style={thStyle}>Partner ID</th>
                                                <th style={thStyle}>Full Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Mobile</th>
                                                <th style={thStyle}>City</th>
                                                <th style={thStyle}>Vehicle Types</th>
                                                <th style={thStyle}>Status</th>
                                                <th style={thStyle}>Verified</th>
                                                <th style={thStyle}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.length ? (
                                                filteredData.map((partner, idx) => (
                                                    <tr key={partner.id || idx}>
                                                        <td style={thTdStyle}>{idx + 1}</td>
                                                        <td style={thTdStyle}>{partner?.partnerId || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.fullName || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.maskedEmail || partner?.email || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.maskedPhoneNumber || partner?.phoneNumber || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.city || "—"}</td>
                                                        <td style={thTdStyle}>
                                                            {Array.isArray(partner?.vehicleTypes)
                                                                ? partner.vehicleTypes.join(", ")
                                                                : "—"}
                                                        </td>
                                                        <td style={thTdStyle}>
                                                            <span className={`badge ${partner.online ? 'bg-primary' : 'bg-secondary'}`}>
                                                                {partner.online ? 'Online' : 'Offline'}
                                                            </span>
                                                        </td>
                                                        <td style={thTdStyle}>
                                                            <span className={`badge ${partner.verified ? 'bg-success' : 'bg-secondary'}`}>
                                                                {partner.verified ? 'Verified' : 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td style={thTdStyle}>
                                                            <button
                                                                style={{ ...buttonStyle, color: "rgb(230 109 51)", fontWeight: "bold" }}
                                                                onClick={() => handleViewClick(partner)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td style={thTdStyle} colSpan={10}>
                                                        {partnersData.length === 0
                                                            ? "Loading data or no partners found."
                                                            : (searchQuery.trim() || statusFilter || vehicleFilter || cityFilter)
                                                                ? "No partners found with the specified filters."
                                                                : "No partners available."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="mt-4 rounded-3 shadow-sm">
                                <div className="d-flex align-items-center mb-4">
                                    <button
                                        onClick={handleBackClick}
                                        className="btn fw-bold px-4 py-2"
                                        style={{ backgroundColor: "rgb(230 109 51)", color: "#fff" }}
                                    >
                                        Back to List
                                    </button>

                                </div>

                                {loading && (
                                    <div className="text-center my-4">
                                        <span className="spinner-border text-primary" />
                                    </div>
                                )}

                                {!loading && (
                                    <>
                                        <div className="register mt-4">
                                            <div className="registertable bo2">
                                                <table className="table table-bordered table-striped align-middle mt-4 w-100">
                                                    <thead>
                                                        <tr>
                                                            <th style={detailThStyle}>Field</th>
                                                            <th style={detailThStyle}>Value</th>
                                                            <th style={detailThStyle}>Image</th>
                                                            <th style={detailThStyle}>Status</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {rows.map((r, i) => {
                                                            const isEditing = r.isEditing || false;

                                                            const handleInputChange = (field, value) => {
                                                                setRows((prevRows) =>
                                                                    prevRows.map((row, idx) =>
                                                                        idx === i
                                                                            ? {
                                                                                ...row,
                                                                                value:
                                                                                    typeof row.value === "object"
                                                                                        ? { ...row.value, [field]: value }
                                                                                        : value,
                                                                            }
                                                                            : row
                                                                    )
                                                                );
                                                            };

                                                            // Handle array selection (multiple checkboxes)
                                                            const handleArraySelection = (selectedValue, arrayField) => {
                                                                setRows((prevRows) =>
                                                                    prevRows.map((row, idx) => {
                                                                        if (idx === i) {
                                                                            const currentValues = Array.isArray(row.value) ? row.value : [];
                                                                            const isSelected = currentValues.includes(selectedValue);

                                                                            if (isSelected) {
                                                                                // Remove if already selected
                                                                                return {
                                                                                    ...row,
                                                                                    value: currentValues.filter(v => v !== selectedValue)
                                                                                };
                                                                            } else {
                                                                                // Add if not selected
                                                                                return {
                                                                                    ...row,
                                                                                    value: [...currentValues, selectedValue]
                                                                                };
                                                                            }
                                                                        }
                                                                        return row;
                                                                    })
                                                                );
                                                            };

                                                            const handleImageChange = (e) => {
                                                                const files = Array.from(e.target.files);
                                                                const newImages = files.map((file) => ({
                                                                    url: URL.createObjectURL(file),
                                                                    file
                                                                }));

                                                                setRows(prev =>
                                                                    prev.map((row, idx) =>
                                                                        idx === i
                                                                            ? {
                                                                                ...row,
                                                                                images: [...(row.images || []), ...newImages]
                                                                            }
                                                                            : row
                                                                    )
                                                                );
                                                            };

                                                            // Get appropriate options based on field
                                                            const getOptions = () => {
                                                                switch (r.key) {
                                                                    case "Vehicle Types": return vehicleTypeOptions;
                                                                    case "Vehicles": return vehiclesOptions;
                                                                    case "Petrolium Types": return petroliumTypeOptions;
                                                                    default: return [];
                                                                }
                                                            };

                                                            return (
                                                                <tr key={r.key || i}>
                                                                    <td className="fw-bold text-center align-middle">{r.key}</td>

                                                                    <td className="text-start align-middle">
                                                                        {r.key === "Vehicle Types" || r.key === "Vehicles" || r.key === "Petrolium Types" ? (
                                                                            isEditing ? (
                                                                                <div className="d-flex flex-column gap-2">
                                                                                    {getOptions().map((option) => {
                                                                                        const currentValues = Array.isArray(r.value) ? r.value : [];
                                                                                        const isSelected = currentValues.includes(option);

                                                                                        return (
                                                                                            <div key={option} className="form-check">
                                                                                                <input
                                                                                                    className="form-check-input"
                                                                                                    type="checkbox"
                                                                                                    id={`${r.key}-${option}-${i}`}
                                                                                                    checked={isSelected}
                                                                                                    onChange={() => handleArraySelection(option, r.key)}
                                                                                                />
                                                                                                <label
                                                                                                    className="form-check-label"
                                                                                                    htmlFor={`${r.key}-${option}-${i}`}
                                                                                                >
                                                                                                    {option}
                                                                                                </label>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            ) : (
                                                                                Array.isArray(r.value) && r.value.length > 0
                                                                                    ? r.value.join(", ")
                                                                                    : "N/A"
                                                                            )
                                                                        ) : r.key === "Bank Details" ? (
                                                                            Object.entries(r.value || {}).map(([field, value]) => (
                                                                                <div key={field} className="mb-1">
                                                                                    <strong>{field.replace(/([A-Z])/g, " $1")}:</strong>{" "}
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={value || ""}
                                                                                            onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                            className="form-control d-inline-block w-75 ms-2"
                                                                                        />
                                                                                    ) : (
                                                                                        safeDisplay(value)
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        ) : r.key === "Current Address" || r.key === "Permanent Address" ? (
                                                                            Object.entries(r.value || {}).map(([field, value]) => (
                                                                                <div key={field} className="mb-1">
                                                                                    <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{" "}
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={value || ""}
                                                                                            onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                            className="form-control d-inline-block w-75 ms-2"
                                                                                        />
                                                                                    ) : (
                                                                                        safeDisplay(value)
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        ) : r.key === "Profile Picture" ? (
                                                                            "Profile Picture"
                                                                        ) : r.key === "RC Number" || r.key === "Insurance Number" ? (
                                                                            isEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    value={r.value || ""}
                                                                                    onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                    className="form-control d-inline-block w-75"
                                                                                    placeholder="Enter comma-separated values"
                                                                                />
                                                                            ) : (
                                                                                safeDisplay(r.value)
                                                                            )
                                                                        ) : (
                                                                            isEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    value={r.value || ""}
                                                                                    onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                    className="form-control d-inline-block w-75"
                                                                                />
                                                                            ) : (
                                                                                safeDisplay(r.value)
                                                                            )
                                                                        )}
                                                                    </td>

                                                                    <td className="text-center align-middle">
                                                                        {isEditing ? (
                                                                            <>
                                                                                <input
                                                                                    type="file"
                                                                                    multiple={r.key === "AADHAR Number" || r.key === "License Number" || r.key === "RC Number"}
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                                <ImageDisplay images={r.images} fieldName={r.key} />
                                                                            </>
                                                                        ) : (
                                                                            <ImageDisplay images={r.images} fieldName={r.key} />
                                                                        )}
                                                                    </td>

                                                                    <td className="text-center align-middle">
                                                                        {isEditing ? (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-success me-2"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleSaveRow(i)}
                                                                                >
                                                                                    Save
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-secondary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() =>
                                                                                        setRows((prev) =>
                                                                                            prev.map((row, idx) =>
                                                                                                idx === i ? { ...row, isEditing: false } : row
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </>
                                                                        ) : (
                                                                            <div style={{ display: "flex", gap: "10px" }}>
                                                                                <button
                                                                                    className={`btn btn-sm ${isFieldVerified(r.key, r.value, partnerDetails)
                                                                                        ? "btn-success"
                                                                                        : "btn-warning"
                                                                                        }`}
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleVerifyy(i)}
                                                                                    disabled={
                                                                                        isFieldVerified(r.key, r.value, partnerDetails) ||
                                                                                        isFieldRejected(r.key, partnerDetails)
                                                                                    }
                                                                                >
                                                                                    {isFieldVerified(r.key, r.value, partnerDetails) ? "Verified" : "Verify"}
                                                                                </button>

                                                                                <button
                                                                                    className={`btn btn-sm ${isFieldRejected(r.key, partnerDetails) ? "btn-danger" : "btn-secondary"
                                                                                        }`}
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleReject(i)}
                                                                                    disabled={
                                                                                        isFieldRejected(r.key, partnerDetails) ||
                                                                                        isFieldVerified(r.key, r.value, partnerDetails)
                                                                                    }
                                                                                >
                                                                                    {isFieldRejected(r.key, partnerDetails) ? "Rejected" : "Reject"}
                                                                                </button>

                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() =>
                                                                                        setRows((prev) =>
                                                                                            prev.map((row, idx) =>
                                                                                                idx === i ? { ...row, isEditing: true } : row
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Approve and Reject Buttons at Bottom */}
                                        <div className="d-flex justify-content-center gap-3 mt-4 mb-4 p-3 border-top position-relative">
                                            <div className="d-flex gap-3 align-items-start position-relative">
                                                {/* Conditionally render Approve button or Approved status */}
                                                {partnerDetails?.verified === true || isApproved ? (
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span className="badge bg-success p-2 fs-6">Approved</span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="btn btn-success px-4 py-2 fw-bold"
                                                        style={{ minWidth: "120px" }}
                                                        onClick={handleApproveAll}
                                                        disabled={isApproving || isRejecting}
                                                    >
                                                        {isApproving ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                                Approving...
                                                            </>
                                                        ) : (
                                                            "Approve"
                                                        )}
                                                    </button>
                                                )}
                                                {/* Conditionally render Reject button or nothing */}
                                                {!(partnerDetails?.verified === true || isApproved) && (
                                                    <div className="position-relative">
                                                        {showRejectDropdown ? (
                                                            <div className="card p-3 shadow-sm" style={{
                                                                width: "300px",
                                                                position: "absolute",
                                                                top: "100%",
                                                                left: "0",
                                                                marginTop: "0px",
                                                                zIndex: 1000
                                                            }}>
                                                                <div className="mb-3">
                                                                    <label className="form-label fw-bold">Rejection Reason</label>
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Enter rejection reason..."
                                                                        value={rejectionDescription}
                                                                        onChange={(e) => setRejectionDescription(e.target.value)}
                                                                        disabled={isApproving || isRejecting}
                                                                    />
                                                                </div>

                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="btn btn-danger flex-grow-1"
                                                                        onClick={handleRejectAll}
                                                                        disabled={!rejectionDescription.trim() || isApproving || isRejecting}
                                                                    >
                                                                        {isRejecting ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                                                Rejecting...
                                                                            </>
                                                                        ) : (
                                                                            "Confirm Reject"
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-secondary"
                                                                        onClick={() => {
                                                                            setShowRejectDropdown(false);
                                                                            setRejectionDescription("");
                                                                        }}
                                                                        disabled={isApproving || isRejecting}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                className="btn btn-danger px-4 py-2 fw-bold"
                                                                style={{ minWidth: "120px" }}
                                                                onClick={() => setShowRejectDropdown(true)}
                                                                disabled={isApproving || isRejecting}
                                                            >
                                                                {isRejecting ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                                        Rejecting...
                                                                    </>
                                                                ) : (
                                                                    "Reject"
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>


                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 5 };
const thTdStyle = { border: "1px solid #ddd", textAlign: "center" };
const thStyle = { ...thTdStyle, backgroundColor: "rgb(230 109 51)", color: "#fff", fontWeight: "bold", maxWidth: "70px" };
const buttonStyle = { backgroundColor: "transparent", border: "none", cursor: "pointer" };

const detailThStyle = {
    ...thTdStyle,
    backgroundColor: "rgb(230 109 51)",
    color: "#fff",
    fontWeight: 600,
    padding: "0.75rem",
    verticalAlign: "middle",
    border: "1px solid #dee2e6",
    textAlign: "center"
};

export default DeliveryPartnerManagement;